import { forwardRef, useState, type ReactNode, type Ref } from 'react';
import KoobButton from '@koob/ui/src/components/KoobButton';
import {
  useListMode,
  useSearchHotels,
  useTripFilters,
} from '../../hooks/useTripStoreSelectors';
import {
  useTripConnectionCurrency,
  useTripFilterOptions,
} from '../../hooks/useTripFilterOptions';
import TripFilterDates from './TripFilterDates';
import TripFilterExperience from './TripFilterExperience';
import TripFilterHotel from './TripFilterHotel';
import TripFilterItinerary from './TripFilterItinerary';
import TripFilterMore from './TripFilterMore';
import TripFilterSearch from './TripFilterSearch';

interface Props {
  t: (key: string) => string;
  // Countries of the trip, to narrow the location search.
  countries?: unknown[];
  locale: string;
  // The user's organisation, to find the currency prices are shown in.
  organizationId?: unknown;
  // The DMC side of that connection, when the trip filters carry no `dmcId`
  // (the DMC app: the user's organisation is the DMC).
  dmcOrganizationId?: unknown;
  // Price currency when there is no organisation to look the connection up
  // with (the DMC app outside a TO trip: its own currency).
  fallbackCurrency?: string;
  // Strip shown above the bar (the app's trip header).
  header?: ReactNode;
  // TO-only: what shows in chat mode, in place of the search bar.
  chatFilters?: ReactNode;
  // TO-only: the "Transition Programs" toggle behind "More filters".
  transitionPrograms?: boolean;
}

// The filter bar of the compose panel: search, dates and the filters of the
// experience or hotel search, plus itinerary and chat modes.
function TripFilters(
  {
    t,
    countries = [],
    locale,
    organizationId,
    dmcOrganizationId,
    fallbackCurrency,
    header,
    chatFilters,
    transitionPrograms,
  }: Props,
  ref: Ref<HTMLDivElement>,
) {
  const tripFilters = useTripFilters();
  const searchHotels = useSearchHotels();
  const listMode = useListMode();
  const [moreFiltersShown, setMoreFiltersShown] = useState(false);
  const options = useTripFilterOptions();
  const connectionCurrency = useTripConnectionCurrency(
    organizationId,
    dmcOrganizationId ?? tripFilters?.dmcId?.value ?? tripFilters?.dmcId,
  );
  const currency = organizationId ? connectionCurrency : fallbackCurrency;

  return (
    <>
      {header}

      {/* No z-index here: as a flex item it would trap the search panel's
          z-modal in a z-10 stacking context, letting the experience cards'
          own z-10 elements (later in the DOM) paint over the open popover. */}
      <div
        ref={ref}
        className="w-full shrink-0 space-y-2 rounded-2xl border border-solid border-k-gray bg-white p-3"
      >
        {!['chat', 'itinerary'].includes(listMode) && (
          <div className="space-y-2">
            <TripFilterSearch t={t} countries={countries} locale={locale} />

            <div className="flex flex-wrap items-center gap-2">
              <TripFilterDates />

              {searchHotels ? (
                <TripFilterHotel
                  t={t}
                  categories={options.categories}
                  kinds={options.kinds}
                  styles={options.styles}
                  sustainableLevels={options.sustainableLevels}
                />
              ) : (
                <>
                  <TripFilterExperience
                    t={t}
                    themes={options.themes}
                    segments={options.segments}
                  />
                  <KoobButton
                    rounded="full"
                    variant={moreFiltersShown ? 'soft' : 'secondary'}
                    onClick={() => setMoreFiltersShown((shown) => !shown)}
                  >
                    <span>{t('tooltip.moreFilters')}</span>
                    <i
                      className={`fa-light fa-chevron-${
                        moreFiltersShown ? 'up' : 'down'
                      }`}
                    />
                  </KoobButton>
                </>
              )}
            </div>

            {!searchHotels && moreFiltersShown && (
              <TripFilterMore
                t={t}
                currency={currency}
                transitionPrograms={transitionPrograms}
              />
            )}
          </div>
        )}

        {listMode === 'chat' && chatFilters}
        {listMode === 'itinerary' && <TripFilterItinerary t={t} />}
      </div>
    </>
  );
}

export default forwardRef(TripFilters);
